import { useState } from 'react'
import { adminService } from 'core/services/adminService'
import { toast } from 'react-toastify'

import c from './assets/sass/advent-edit.module.scss'

const REWARD_TYPES = [
    { value: 'COINS', label: '💰 Монеты' },
    { value: 'ENERGY', label: '⚡ Энергия' },
    { value: 'PROMO_CASE', label: '🎁 Промокод на кейс' },
    { value: 'PROMO_SKIN', label: '🎨 Промокод на скин' },
    { value: 'PROMO_DEPOSIT', label: '💳 Промокод на депозит' },
    { value: 'PROMO_WHEEL', label: '🎰 Промокод на вращение' },
    { value: 'DEPOSIT_FREE_CASE', label: '📦 Бесплатный кейс при депозите' },
    { value: 'PROMO_XP', label: '🌟 Промокод на XP' },
    { value: 'PROMO_BALANCE', label: '💵 Промокод на баланс' },
    { value: 'GIFT', label: '🎉 Суперприз' },
]

// Многоразовые типы наград (могут выпадать всем юзерам)
const REUSABLE_TYPES = [
    'COINS',
    'ENERGY',
    'PROMO_DEPOSIT',
    'DEPOSIT_FREE_CASE',
    'PROMO_WHEEL',
]

// Индивидуальные типы (1 промокод = 1 юзер)
const INDIVIDUAL_TYPES = [
    'PROMO_CASE',
    'PROMO_SKIN',
    'PROMO_BALANCE',
    'PROMO_XP',
    'GIFT',
]

export const AdventEdit = ({ reward, onSave, onCancel }) => {
    const [day, setDay] = useState(reward?.day || 1)
    const [isEditMode] = useState(!!reward)
    const [editingRewardId] = useState(reward?.id || null)
    const [rewards, setRewards] = useState(
        reward
            ? [
                  {
                      id: Date.now(),
                      rewardType: reward?.rewardType || 'COINS',
                      reward: reward?.reward || '0',
                      description: reward?.description || '',
                      promoCode: reward?.promoCode || '',
                      isActive: reward?.isActive ?? true,
                      chance: reward?.chance || 100,
                      isReusable:
                          reward?.isReusable ??
                          REUSABLE_TYPES.includes(reward?.rewardType),
                      maxActivations: reward?.maxActivations || 0,
                      promoCodes: '',
                      txtCodes: [],
                  },
              ]
            : [
                  {
                      id: Date.now(),
                      rewardType: 'COINS',
                      reward: '0',
                      description: '',
                      promoCode: '',
                      isActive: true,
                      chance: 100,
                      isReusable: true,
                      maxActivations: 0,
                      promoCodes: '',
                      txtCodes: [],
                  },
              ]
    )
    const [loading, setLoading] = useState(false)

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            setLoading(true)

            // Сохраняем/обновляем каждую награду
            for (let i = 0; i < rewards.length; i++) {
                const r = rewards[i]

                // Если это редактирование первой награды (оригинальной)
                if (isEditMode && i === 0 && editingRewardId) {
                    await adminService.updateAdventReward(editingRewardId, {
                        day,
                        rewardType: r.rewardType,
                        reward: r.reward,
                        description: r.description,
                        promoCode: r.promoCode,
                        isActive: r.isActive,
                        chance: r.chance,
                        isReusable: r.isReusable,
                        maxActivations: parseInt(r.maxActivations) || 0,
                    })
                } else {
                    // Создаем новую награду
                    await adminService.createAdventReward({
                        day,
                        rewardType: r.rewardType,
                        reward: r.reward,
                        description: r.description,
                        promoCode: r.promoCode,
                        isActive: r.isActive,
                        chance: r.chance,
                        isReusable: r.isReusable,
                        maxActivations: parseInt(r.maxActivations) || 0,
                    })
                }
            }

            if (isEditMode) {
                toast(
                    `Награда обновлена${rewards.length > 1 ? ` и добавлено ${rewards.length - 1} новых` : ''}`,
                    { type: 'success' }
                )
            } else {
                toast(`Создано ${rewards.length} наград для дня ${day}`, {
                    type: 'success',
                })
            }

            onSave()
        } catch (error) {
            toast('Ошибка: ' + error.message, { type: 'error' })
        } finally {
            setLoading(false)
        }
    }

    const addReward = () => {
        if (rewards.length >= 40) {
            toast('Максимум 40 наград на день', { type: 'warning' })
            return
        }
        setRewards([
            ...rewards,
            {
                id: Date.now(),
                rewardType: 'COINS',
                reward: '0',
                description: '',
                promoCode: '',
                isActive: true,
                chance: 100,
                isReusable: true,
                maxActivations: 0,
                promoCodes: '',
                txtCodes: [],
            },
        ])
    }

    const removeReward = (id) => {
        if (rewards.length === 1) {
            toast('Должна быть минимум одна награда', { type: 'warning' })
            return
        }
        setRewards(rewards.filter((r) => r.id !== id))
    }

    const updateReward = (id, updates) => {
        setRewards(
            rewards.map((r) => {
                if (r.id !== id) return r

                const updated = { ...r, ...updates }

                // Автоматически устанавливаем isReusable при смене типа награды
                if (updates.rewardType) {
                    const isReusable = REUSABLE_TYPES.includes(
                        updates.rewardType
                    )
                    const isIndividual = INDIVIDUAL_TYPES.includes(
                        updates.rewardType
                    )

                    updated.isReusable = isReusable

                    // Для многоразовых устанавливаем maxActivations = 0 (безлимит) если не указано
                    if (isReusable && !updated.maxActivations) {
                        updated.maxActivations = 0
                    }
                    // Для индивидуальных устанавливаем maxActivations = 1
                    if (isIndividual) {
                        updated.isReusable = false
                        updated.maxActivations = 1
                    }
                }

                return updated
            })
        )
    }

    const handleTxtFileChange = (rewardId, e) => {
        const file = e.target.files[0]
        if (!file) return

        const reader = new FileReader()
        reader.onload = (event) => {
            const text = event.target.result
            const codes = text
                .split('\n')
                .map((c) => c.trim())
                .filter(Boolean)
            updateReward(rewardId, { txtCodes: codes })
        }
        reader.readAsText(file)
    }

    const handleCreateBulk = async (rewardData) => {
        const codes =
            rewardData.txtCodes.length > 0
                ? rewardData.txtCodes
                : rewardData.promoCodes
                      .split('\n')
                      .map((c) => c.trim())
                      .filter(Boolean)

        if (codes.length === 0) {
            toast('Добавьте промокоды', { type: 'warning' })
            return
        }

        try {
            setLoading(true)
            await adminService.createAdventRewardsBulk({
                day,
                rewardType: rewardData.rewardType,
                reward: rewardData.reward,
                description: rewardData.description,
                promoCodes: codes,
                chance: rewardData.chance,
                isActive: rewardData.isActive,
                isReusable: rewardData.isReusable,
                maxActivations: parseInt(rewardData.maxActivations) || 0,
            })

            // Показываем сообщение в зависимости от типа награды
            if (rewardData.isReusable) {
                toast(
                    `✅ Создана 1 многоразовая награда (промокод: ${codes[0]})`,
                    { type: 'success' }
                )
            } else {
                toast(`✅ Создана 1 награда с ${codes.length} промокодами`, {
                    type: 'success',
                })
            }

            // Очищаем поля промокодов, но НЕ закрываем окно
            updateReward(rewardData.id, { promoCodes: '', txtCodes: [] })

            // НЕ вызываем onSave() - окно остается открытым
        } catch (error) {
            toast('Ошибка: ' + error.message, { type: 'error' })
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className={c.component}>
            <h3>
                {isEditMode
                    ? `Редактирование наград дня ${day}`
                    : `Создание наград для дня ${day}`}
            </h3>

            <div className={c.daySelector}>
                <label>
                    День (1-31):
                    <input
                        type="number"
                        min="1"
                        max="31"
                        value={day || 1}
                        onChange={(e) => setDay(parseInt(e.target.value) || 1)}
                        required
                        disabled={isEditMode}
                    />
                </label>
                {isEditMode && (
                    <p
                        style={{
                            fontSize: '12px',
                            color: 'rgba(255,255,255,0.6)',
                            margin: '8px 0 0 0',
                        }}
                    >
                        💡 Можно добавить до 40 наград. Первая награда
                        обновится, остальные добавятся новыми.
                    </p>
                )}
            </div>

            <form onSubmit={handleSubmit} className={c.form}>
                {rewards.map((r, index) => (
                    <div key={r.id} className={c.rewardBlock}>
                        <div className={c.rewardHeader}>
                            <h4>
                                Награда {index + 1}{' '}
                                {isEditMode && index === 0
                                    ? '(редактируется)'
                                    : isEditMode
                                      ? '(новая)'
                                      : ''}
                            </h4>
                            {rewards.length > 1 &&
                                (isEditMode ? index > 0 : true) && (
                                    <button
                                        type="button"
                                        onClick={() => removeReward(r.id)}
                                        className={c.removeBtn}
                                    >
                                        ✕
                                    </button>
                                )}
                        </div>
                        <label>
                            Тип награды:
                            <select
                                value={r.rewardType}
                                onChange={(e) =>
                                    updateReward(r.id, {
                                        rewardType: e.target.value,
                                    })
                                }
                            >
                                {REWARD_TYPES.map((type) => (
                                    <option key={type.value} value={type.value}>
                                        {type.label}
                                    </option>
                                ))}
                            </select>
                        </label>

                        {/* Показываем тип награды */}
                        <div
                            style={{
                                padding: '8px 12px',
                                background: r.isReusable
                                    ? 'rgba(76,175,80,0.1)'
                                    : 'rgba(33,150,243,0.1)',
                                borderRadius: '6px',
                                fontSize: '12px',
                                color: r.isReusable ? '#4CAF50' : '#2196F3',
                                marginBottom: '12px',
                            }}
                        >
                            {r.isReusable ? (
                                <>
                                    🔄 <strong>Многоразовая награда</strong> -
                                    может выпадать всем юзерам (лимит:{' '}
                                    {r.maxActivations || 'безлимит'})
                                </>
                            ) : (
                                <>
                                    🎯 <strong>Индивидуальная награда</strong> -
                                    каждый промокод для 1 юзера
                                </>
                            )}
                        </div>

                        {(r.rewardType === 'COINS' ||
                            r.rewardType === 'ENERGY') && (
                            <label>
                                {r.rewardType === 'COINS'
                                    ? 'Количество монет:'
                                    : 'Количество энергии:'}
                                <input
                                    type="number"
                                    value={r.reward || ''}
                                    onChange={(e) =>
                                        updateReward(r.id, {
                                            reward: e.target.value || '0',
                                        })
                                    }
                                    required
                                />
                            </label>
                        )}

                        {r.rewardType !== 'COINS' &&
                            r.rewardType !== 'ENERGY' && (
                                <>
                                    {r.isReusable ? (
                                        <>
                                            <label>
                                                Промокод (многоразовый):
                                                <input
                                                    type="text"
                                                    value={r.promoCode || ''}
                                                    onChange={(e) =>
                                                        updateReward(r.id, {
                                                            promoCode:
                                                                e.target.value,
                                                        })
                                                    }
                                                    placeholder="Один промокод для всех"
                                                />
                                            </label>
                                            {isEditMode &&
                                                reward?.currentActivations >
                                                    0 && (
                                                    <div
                                                        style={{
                                                            padding: '8px 12px',
                                                            background:
                                                                'rgba(76,175,80,0.1)',
                                                            borderRadius: '6px',
                                                            fontSize: '12px',
                                                            color: '#4CAF50',
                                                        }}
                                                    >
                                                        ✅ Использований:{' '}
                                                        {
                                                            reward.currentActivations
                                                        }
                                                        /
                                                        {reward.maxActivations ||
                                                            '∞'}
                                                    </div>
                                                )}
                                        </>
                                    ) : (
                                        <>
                                            {isEditMode &&
                                                reward?.promoStats && (
                                                    <div
                                                        style={{
                                                            padding: '8px 12px',
                                                            background:
                                                                reward
                                                                    .promoStats
                                                                    .available >
                                                                0
                                                                    ? 'rgba(76,175,80,0.1)'
                                                                    : 'rgba(244,67,54,0.1)',
                                                            borderRadius: '6px',
                                                            fontSize: '12px',
                                                            color:
                                                                reward
                                                                    .promoStats
                                                                    .available >
                                                                0
                                                                    ? '#4CAF50'
                                                                    : '#f44336',
                                                            marginBottom:
                                                                '12px',
                                                        }}
                                                    >
                                                        📊 Промокодов:{' '}
                                                        {
                                                            reward.promoStats
                                                                .available
                                                        }
                                                        /
                                                        {
                                                            reward.promoStats
                                                                .total
                                                        }
                                                        {reward.promoStats
                                                            .isSharedPool && (
                                                            <div
                                                                style={{
                                                                    marginTop:
                                                                        '4px',
                                                                    fontSize:
                                                                        '11px',
                                                                    opacity: 0.8,
                                                                }}
                                                            >
                                                                🔗 Общий пул с
                                                                другими днями
                                                                (используются
                                                                глобально)
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                        </>
                                    )}
                                </>
                            )}

                        <label>
                            Описание:
                            <textarea
                                value={r.description || ''}
                                onChange={(e) =>
                                    updateReward(r.id, {
                                        description: e.target.value,
                                    })
                                }
                                required
                                rows={2}
                            />
                        </label>

                        <label>
                            Шанс выпадения (%):
                            <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.1"
                                value={r.chance || 100}
                                onChange={(e) =>
                                    updateReward(r.id, {
                                        chance:
                                            parseFloat(e.target.value) || 100,
                                    })
                                }
                                required
                            />
                        </label>

                        <div
                            style={{
                                display: 'flex',
                                gap: '16px',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                            }}
                        >
                            <label className={c.checkbox}>
                                <input
                                    type="checkbox"
                                    checked={r.isActive}
                                    onChange={(e) =>
                                        updateReward(r.id, {
                                            isActive: e.target.checked,
                                        })
                                    }
                                />
                                Активна
                            </label>

                            <label className={c.checkbox}>
                                <input
                                    type="checkbox"
                                    checked={r.isReusable}
                                    onChange={(e) =>
                                        updateReward(r.id, {
                                            isReusable: e.target.checked,
                                            maxActivations: e.target.checked
                                                ? 0
                                                : 1,
                                        })
                                    }
                                />
                                Многоразовая
                            </label>

                            {r.isReusable && (
                                <label style={{ flex: '1 1 200px' }}>
                                    Лимит активаций (0 = безлимит):
                                    <input
                                        type="number"
                                        min="0"
                                        value={r.maxActivations || 0}
                                        onChange={(e) =>
                                            updateReward(r.id, {
                                                maxActivations:
                                                    parseInt(e.target.value) ||
                                                    0,
                                            })
                                        }
                                        placeholder="0 = безлимит"
                                    />
                                </label>
                            )}
                        </div>

                        {r.rewardType !== 'COINS' &&
                            r.rewardType !== 'ENERGY' &&
                            !r.isReusable && (
                                <div className={c.bulk}>
                                    <h4>+ Массовое добавление промокодов</h4>
                                    <p>
                                        {r.isReusable
                                            ? 'Для многоразовой награды используйте поле "Промокод" выше'
                                            : 'Создастся 1 награда с указанными промокодами (каждый промокод для 1 юзера)'}
                                    </p>
                                    <textarea
                                        value={r.promoCodes}
                                        onChange={(e) =>
                                            updateReward(r.id, {
                                                promoCodes: e.target.value,
                                            })
                                        }
                                        placeholder="Один промокод на строку"
                                        rows={4}
                                    />
                                    <input
                                        type="file"
                                        accept=".txt"
                                        onChange={(e) =>
                                            handleTxtFileChange(r.id, e)
                                        }
                                    />
                                    {r.txtCodes && r.txtCodes.length > 0 && (
                                        <p
                                            style={{
                                                fontSize: '13px',
                                                color: '#4CAF50',
                                            }}
                                        >
                                            Загружено {r.txtCodes.length}{' '}
                                            промокодов из файла
                                        </p>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => handleCreateBulk(r)}
                                        disabled={loading}
                                    >
                                        {r.isReusable
                                            ? `Добавить ${r.txtCodes?.length || r.promoCodes.split('\n').filter((c) => c.trim()).length || 0} активаций`
                                            : `Добавить ${r.txtCodes?.length || r.promoCodes.split('\n').filter((c) => c.trim()).length || 0} промокодов к награде`}
                                    </button>
                                </div>
                            )}
                    </div>
                ))}

                {rewards.length < 40 && (
                    <button
                        type="button"
                        onClick={addReward}
                        className={c.addRewardBtn}
                    >
                        + Добавить еще награду (всего: {rewards.length}/40)
                    </button>
                )}

                <div className={c.buttons}>
                    <button type="submit" disabled={loading}>
                        {reward
                            ? 'Сохранить'
                            : `Создать ${rewards.length} ${rewards.length === 1 ? 'награду' : 'наград'}`}
                    </button>
                    <button type="button" onClick={onCancel}>
                        Отмена
                    </button>
                </div>
            </form>
        </div>
    )
}
